import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  CheckCircle,
  Sparkles,
  Phone,
} from "lucide-react";
import { LOAN_PROGRAMS } from "@/data/loanPrograms";
import SEO from "@/components/SEO";
import { buildGraph, webPageJsonLd, faqJsonLd, breadcrumbJsonLd, loanServiceJsonLd } from "@/data/seo";

const RED = "#D41F30";

export default function LoanProgramPage() {
  const { slug } = useParams();
  const program = LOAN_PROGRAMS[slug];

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white pt-28" data-testid="loan-not-found">
        <div className="text-center">
          <h1 className="font-display text-4xl font-black text-gray-900 mb-3">Loan program not found</h1>
          <p className="text-gray-500 mb-6">We couldn't find that loan type.</p>
          <Button
            className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-3"
            onClick={() => window.location.assign("/")}
          >
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  const { name, eyebrow, Icon, headline, subhead, heroImage, benefits, requirements, bestFor, faqs } = program;

  return (
    <div className="bg-white" data-testid={`loan-page-${slug}`}>
      <SEO
        title={`${name} | Requirements, rates, and how to apply`}
        description={`${subhead.slice(0, 155)}`}
        path={`/loans/${slug}`}
        ogImage={heroImage}
        jsonLd={buildGraph(
          webPageJsonLd({
            name: `${name} | CityWorth`,
            description: subhead,
            path: `/loans/${slug}`,
          }),
          loanServiceJsonLd(program),
          faqJsonLd(faqs),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Loan Options", path: "/" },
            { name: name, path: `/loans/${slug}` },
          ])
        )}
      />
      {/* Hero */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-6">
                <Icon className="w-4 h-4" style={{ color: RED }} />
                <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
                  {eyebrow}
                </span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.05] tracking-tight mb-6">
                {name}
              </h1>
              <p className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-tight">
                {headline}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-2xl">
                {subhead}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  onClick={() => window.location.assign("/get-started")}
                  data-testid={`loan-${slug}-cta`}
                >
                  Start my homebuying plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-8 py-7 text-lg font-semibold"
                  onClick={() => document.getElementById("benefits").scrollIntoView({ behavior: "smooth" })}
                >
                  Learn more
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img src={heroImage} alt={name} className="w-full h-[420px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3" style={{ color: RED }}>
              Why buyers choose this loan
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 leading-tight">
              The benefits of {name}s
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`benefit-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                  <CheckCircle className="w-5 h-5" style={{ color: RED }} />
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-2">{b.title}</h3>
                <p className="text-gray-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements & best for */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3" style={{ color: RED }}>
              At a glance
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-gray-900 mb-8 leading-tight">
              What you'll need
            </h2>
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-2xl overflow-hidden">
              {requirements.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-5 bg-white">
                  <span className="text-gray-600">{r.label}</span>
                  <span className="font-bold text-gray-900">{r.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3" style={{ color: RED }}>
              Best for
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-gray-900 mb-6 leading-tight">
              Is this loan right for you?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">{bestFor}</p>
            <div className="rounded-2xl bg-gradient-to-br from-red-50 to-white border border-red-100 p-7">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#D41F30] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-display text-xl font-bold text-gray-900 mb-2">
                    Not sure which loan fits?
                  </p>
                  <p className="text-gray-600 mb-4">
                    Take our 2-minute homebuying plan and we'll match you with the right program.
                  </p>
                  <Button
                    className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-3 text-sm font-semibold"
                    onClick={() => window.location.assign("/get-started")}
                  >
                    Get matched
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 md:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3 text-center" style={{ color: RED }}>
            Questions, answered
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 mb-12 text-center leading-tight">
            {name} FAQs
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white rounded-2xl border border-gray-100 px-6 shadow-sm"
              >
                <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-gray-900 hover:text-[#D41F30] py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-[1.05] tracking-tight">
            Ready to see if you qualify?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Start your homebuying plan, 2 minutes, no credit check, no commitment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-10 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              onClick={() => window.location.assign("/get-started")}
              data-testid={`loan-${slug}-cta-bottom`}
            >
              Start my homebuying plan
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-8 py-7 text-lg font-semibold"
              onClick={() => window.open("tel:+15715551234", "_self")}
            >
              <Phone className="mr-2 w-5 h-5" />
              Talk to an expert
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
